import {
	useContext,
	memo,
	useMemo,
	useCallback,
	useState,
	useRef,
	useEffect,
} from "react";
import { FiltersCtx } from "./Dashboard";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import * as containmentFilter from "enums/filters/containment";
import {
	Box,
	Button,
	styled,
	Chip,
	alpha,
	Paper,
	Theme,
	Tooltip,
} from "@mui/material";
import * as util from "lib/util";
import { FilterField, FilterTextField, MotionGrid } from "components";
import { DateFilterComponent } from "components";
import { DropdownFilter } from "components/DropdownFilter";
import { DashboardFilters, DashboardFiltersCtx } from "./types";
import { motion, Variants } from "framer-motion";
import * as hooks from "lib/hooks";
import { FilterEnum } from "enums/filterEnum";
import { ContainmentFilterKey } from "enums/filters";
import { KeyboardArrowDown } from "@mui/icons-material";

// Enhanced Material You TextField styling
/* eslint-disable-next-line */

// Material You Filter Count Chip
/* eslint-disable-next-line */
const FilterCountChip = styled(Chip)(({ theme }: { theme: Theme }) => {
	const materialColors = hooks.UseMaterialColors();
	return {
		"borderRadius": materialColors.shape.corner.full,
		"height": 28,
		"fontWeight": 500,
		"fontSize": "0.75rem",
		"backgroundColor": alpha(materialColors.primary, 0.12),
		"color": materialColors.primary,
		"border": `1px solid ${alpha(materialColors.primary, 0.2)}`,
		"transition": "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
		"& .MuiChip-label": {
			padding: theme.spacing(0, 1),
		},
		"&:hover": {
			backgroundColor: alpha(materialColors.primary, 0.16),
		},
		"& .MuiChip-deleteIcon": {
			"color": materialColors.primary,
			"margin": theme.spacing(0, 0.5, 0, -0.25),
			"transition": "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
			"&:hover": {
				color: materialColors.primaryContainer,
			},
		},
	};
});

// Animation variants with Material motion
const containerVariants: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.03,
			duration: 0.4,
			ease: [0.4, 0, 0.2, 1], // Material standard easing
		},
	},
};

const itemVariants: Variants = {
	hidden: { y: 8, opacity: 0 },
	visible: {
		y: 0,
		opacity: 1,
		transition: {
			type: "spring",
			stiffness: 300,
			damping: 24,
		},
	},
};

// Header animations
const headerVariants: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.05,
			ease: [0.4, 0, 0.2, 1],
		},
	},
};

const headerItemVariants: Variants = {
	hidden: { opacity: 0, x: -5 },
	visible: {
		opacity: 1,
		x: 0,
		transition: {
			duration: 0.3,
			ease: [0.4, 0, 0.2, 1],
		},
	},
};

// Memoized components with proper types
interface searchFieldProps {
	filterCtx: DashboardFiltersCtx;
	materialColors: ReturnType<typeof hooks.UseMaterialColors>;
}

/* eslint-disable-next-line */
const SearchField = memo(({ filterCtx, materialColors }: searchFieldProps) => {
	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			filterCtx.setFilter({ q: e.target.value });
		},
		[filterCtx]
	);

	return (
		<FilterTextField
			fullWidth
			sx={{ maxWidth: 400 }}
			inputRef={(el: HTMLInputElement | null) => {
				if (filterCtx.filterRefs?.current?.q && el) {
					filterCtx.filterRefs.current.q.current = el;
				}
			}}
			defaultValue={
				filterCtx.getFilterVal("q" as keyof DashboardFilters) || ""
			}
			onChange={handleSearchChange}
			placeholder="Search..."
			variant="outlined"
		/>
	);
});
SearchField.displayName = "SearchField";

interface textFilterFieldProps {
	field: ContainmentFilterKey;
	label: string;
	filterCtx: DashboardFiltersCtx;
}

/* eslint-disable-next-line */
const TextFilterField = memo(
	({ field, label, filterCtx }: textFilterFieldProps) => {
		const isEditing = filterCtx.editingField === field;
		const value = (filterCtx.filters.filters[
			field as keyof DashboardFilters
		] || "") as string;

		const handleEdit = useCallback(() => {
			filterCtx.setEditingField(field as keyof DashboardFilters);
		}, [filterCtx, field]);

		const handleChange = useCallback(
			(newValue: string) => {
				filterCtx.setFilter({ [field]: newValue || null });
			},
			[filterCtx, field]
		);

		const handleSubmit = useCallback(() => {
			filterCtx.setEditingField(null);
		}, [filterCtx]);

		return (
			<FilterField
				inputRef={(el: HTMLInputElement | null) => {
					if (filterCtx.filterRefs?.current?.[field] && el) {
						filterCtx.filterRefs.current[field].current = el;
					}
				}}
				isEditing={isEditing}
				defaultValue={
					filterCtx.getFilterVal(field as keyof DashboardFilters) ||
					""
				}
				onEdit={handleEdit}
				placeholder={label}
				onChange={handleChange}
				onSubmit={handleSubmit}
			/>
		);
	}
);
TextFilterField.displayName = "TextFilterField";

/* eslint-disable-next-line */
interface dateFilterProps {
	field: string;
	label: string;
	filterCtx: DashboardFiltersCtx;
}

/* eslint-disable-next-line */
const DateFilter = memo(({ field, label, filterCtx }: dateFilterProps) => {
	const handleChange = useCallback(
		(v: any) => {
			filterCtx.setDateParam(field as keyof DashboardFilters, v);
		},
		[filterCtx, field]
	);

	const value = filterCtx.filters
		.Get(field as keyof typeof filterCtx.filters)
		.Expect(`${field} not found`);

	return (
		<DateFilterComponent
			label={label}
			onChange={handleChange}
			value={value}
		/>
	);
});
DateFilter.displayName = "DateFilter";

interface memoizedDropdownFilterProps {
	filterType: FilterEnum<ContainmentFilterKey, any>;
	filterCtx: DashboardFiltersCtx;
	textFieldProps: {
		placeholder: string;
	};
}

/* eslint-disable-next-line */
const MemoizedDropdownFilter = memo(
	({
		filterType,
		filterCtx,
		textFieldProps,
	}: memoizedDropdownFilterProps) => {
		const handleChange = useCallback(
			({
				strValue,
				field,
			}: {
				strValue: () => string;
				field: string;
			}) => {
				filterCtx.setFilter({ [field]: strValue() });
			},
			[filterCtx]
		);

		const getValue = useCallback(
			(props: { field: string; data: any[] }) => {
				const val = filterCtx.filters.filters.Get<DashboardFilters>(
					props.field as keyof DashboardFilters
				);

				return val
					.Map((rawValue: any) => {
						if (!rawValue) {
							return filterType.Data.multiple ? [] : null;
						}

						if (filterType.Data.multiple) {
							const splitVals = rawValue.Split(
								util.SearchQueryDelimiter
							);

							const mapped = splitVals
								.Map((v: string) => v.ToLower())
								.FilterMap((curr: string) =>
									props.data.Find((d: any) =>
										d.Name.ToLower().Equal(curr)
									)
								);

							return mapped;
						}

						const single = props.data
							.Find((d: any) =>
								d.Name.ToLower().Equal(rawValue.ToLower())
							)
							.UnwrapOr(null as any);
						return single;
					})
					.UnwrapOr(filterType.Data.multiple ? [] : null);
			},
			[filterCtx.filters.filters, filterType.Data.multiple]
		);

		return (
			<DropdownFilter
				filterType={filterType}
				getValue={getValue}
				onChange={handleChange}
				keyFunc={(o: any) => o.Name.ToLower()}
				labelFunc={(o: any) => o.Name}
				textFieldProps={textFieldProps}
			/>
		);
	}
);
MemoizedDropdownFilter.displayName = "MemoizedDropdownFilter";

function dashboardFiltersComponentBase() {
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const [openFilter, setOpenFilter] = useState<string | null>(null);

	const toggleFilter = (field: string) => {
		setOpenFilter((prev) => (prev === field ? null : field));
	};

	const filterCtx = useContext(FiltersCtx);
	const materialColors = hooks.UseMaterialColors();

	// Count active filters
	const activeFiltersCount = useMemo(
		() =>
			filterCtx.filters.filters
				? Object.keys(filterCtx.filters.filters).length
				: 0,
		[filterCtx.filters.filters]
	);

	// Memoize filter configurations
	const textFilters = useMemo(
		() =>
			containmentFilter.ContainmentSearchFields.Filter((ft) =>
				ft.Match({
					Text: () => true,
					Date: () => false,
					Dropdown: () => false,
				})
			),
		[]
	);

	const dateFilters = useMemo(
		() =>
			containmentFilter.ContainmentSearchFields.Filter((ft) =>
				ft.Is("Date")
			),
		[]
	);

	const dropdownFilters = useMemo(
		() =>
			containmentFilter.ContainmentSearchFields.Filter((ft) =>
				ft.Match({
					Dropdown: () => true,
					Date: () => false,
					Text: () => false,
				})
			),
		[]
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;

			// If click is inside the dropdown (AntD uses .ant-select-dropdown), do nothing
			if (
				target.closest(".ant-select-dropdown, .ant-picker-dropdown") ||
				dropdownRef.current?.contains(target)
			) {
				return;
			}

			setOpenFilter(null);
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<Paper
			component={motion.div}
			variants={containerVariants}
			initial="hidden"
			animate="visible"
			elevation={1}
			sx={{
				mb: 2,
				px: 2,
				py: 1,
				borderRadius: `8px`,
				overflow: "visible",
				bgcolor: materialColors.isDark
					? materialColors.surface[1]
					: materialColors.surface[0],
				border: `1px solid ${alpha(materialColors.outline, 0.08)}`,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1,
					minWidth: "100%",
				}}
			>
				<FilterListIcon
					sx={{
						color: materialColors.neutral,
						fontSize: 20,
					}}
				/>

				{/* Search field - fills remaining space */}
				<Box sx={{ flex: 1, minWidth: 180, pr: 3 }}>
					<SearchField
						filterCtx={filterCtx}
						materialColors={materialColors}
					/>
				</Box>

				{[...textFilters, ...dropdownFilters].Map((ft) => {
					const id = ft.Data.field;
					const label = ft.Data.label;
					const field = ft.Data.field;
					const isOpen = openFilter === id;

					const renderFilterField = () =>
						ft.Match({
							Text: () => (
								<TextFilterField
									field={id}
									label={label}
									filterCtx={filterCtx}
								/>
							),
							Dropdown: () => (
								<MemoizedDropdownFilter
									filterType={ft}
									filterCtx={filterCtx}
									textFieldProps={{
										placeholder: (() => {
											const val =
												filterCtx.getFilterVal(field);
											if (!val)
												return `Select ${util.ProperCase(label)}...`;
											return (
												val?.toString?.() ??
												`Select ${util.ProperCase(label)}...`
											);
										})(),
									}}
								/>
							),
							Date: () => (
								<DateFilter
									field={id}
									label={label}
									filterCtx={filterCtx}
								/>
							),
						});

					return (
						<Box
							key={id}
							sx={{
								position: "relative",
								backgroundColor: "transparent",
							}}
						>
							<Button
								onClick={() => toggleFilter(id)}
								endIcon={
									<KeyboardArrowDown sx={{ fontSize: 18 }} />
								}
								sx={{
									"backgroundColor": "transparent",
									"fontWeight": filterCtx.getFilterVal(field)
										? "bold"
										: "normal",
									"color": materialColors.isDark
										? "#fff"
										: "#000",
									"fontSize": "0.75rem",
									"&:hover": {
										backgroundColor: alpha(
											materialColors.onSurfaceVariant,
											0.08
										),
									},
									"px": 1,
									"py": 0.5,
								}}
							>
								{label}
							</Button>
							{isOpen && (
								<Box
									ref={dropdownRef}
									sx={{
										mt: 1,
										p: 0.5,
										backgroundColor:
											materialColors.surface[0],
										boxShadow: 3,
										borderRadius: 1,
										minWidth: 240,
										zIndex: 10,
										position: "absolute",
										...(ft.Is("Date") && { right: 0 }),
									}}
								>
									{renderFilterField()}
								</Box>
							)}
						</Box>
					);
				})}

				{[...dateFilters].Map((ft) => {
					const id = ft.Data.field;
					const label = ft.Data.label;
					const field = ft.Data.field;
					const isOpen = openFilter === id;

					const filterVal = filterCtx.filters[field];

					const isDateFilterActive =
						typeof filterVal === "object" &&
						filterVal !== null &&
						"tag" in filterVal &&
						(filterVal as any).tag !== "AllTime";

					const fontWeight = isDateFilterActive ? "bold" : "normal";

					const renderFilterField = () =>
						ft.Match({
							Text: () => (
								<TextFilterField
									field={id}
									label={label}
									filterCtx={filterCtx}
								/>
							),
							Dropdown: () => (
								<MemoizedDropdownFilter
									filterType={ft}
									filterCtx={filterCtx}
									textFieldProps={{
										placeholder: `Select ${util.ProperCase(label)}...`,
									}}
								/>
							),
							Date: () => (
								<DateFilter
									field={id}
									label={label}
									filterCtx={filterCtx}
								/>
							),
						});

					return (
						<Box
							key={id}
							sx={{
								position: "relative",
								backgroundColor: "transparent",
							}}
						>
							<Button
								onClick={() => toggleFilter(id)}
								endIcon={
									<KeyboardArrowDown sx={{ fontSize: 18 }} />
								}
								sx={{
									"backgroundColor": "transparent",
									fontWeight,
									"color": materialColors.isDark
										? "#fff"
										: "#000",
									"fontSize": "0.75rem",
									"&:hover": {
										backgroundColor: alpha(
											materialColors.onSurfaceVariant,
											0.08
										),
									},
									"px": 1,
									"py": 0.5,
								}}
							>
								{label}
							</Button>
							{isOpen && (
								<Box
									ref={dropdownRef}
									sx={{
										mt: 1,
										p: 0.5,
										backgroundColor:
											materialColors.surface[0],
										boxShadow: 3,
										borderRadius: 1,
										minWidth: 420,
										zIndex: 10,
										position: "absolute",
										...(ft.Is("Date") && { right: 0 }),
									}}
								>
									{renderFilterField()}
								</Box>
							)}
						</Box>
					);
				})}

				{/* Clear All button */}
				{
					<Button
						disabled={!filterCtx.filtersActive}
						component={motion.button}
						variants={headerItemVariants}
						variant="text"
						size="small"
						onClick={filterCtx.resetFilters}
						sx={{
							"textTransform": "none",
							"color": materialColors.onSurfaceVariant,
							"fontSize": "0.75rem",
							"py": 0.5,
							"px": 0.5,
							"minHeight": "28px",
							"borderRadius": "4px",
							"&:hover": {
								backgroundColor: alpha(
									materialColors.primary,
									0.08
								),
								color: materialColors.onSurface,
							},
						}}
					>
						<Tooltip title="Clear All Filters">
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<ClearIcon
									sx={{
										fontSize: 16,
									}}
								/>
								<span style={{ fontWeight: "bold" }}>
									Clear All
								</span>
							</Box>
						</Tooltip>
					</Button>
				}
			</Box>
		</Paper>
	);
}

// Create memoized component
export const DashboardFiltersComponent = memo(dashboardFiltersComponentBase);
